import {
    useCallback,
    useLayoutEffect, useMemo,
    useRef,
    useState,
} from 'react'
import useLocalStorage, {
    LocalStorageKeys,
} from 'app/hooks/useLocalStorage'
import {
    SkyAccordionLocalStorage,
} from './types'

const usePersistedResize = ({
    persistentKey,
    initialHeightPx,
    enablePersistence = true,
}:{
    persistentKey: string,
    initialHeightPx: number,
    enablePersistence?: boolean,
}) => {
    const containerRef = useRef(null)
    const initialHeightState = useMemo(() => {
        return {
            tableHeightPx: initialHeightPx,
        }
    }, [initialHeightPx])
    const {
        dataGridState,
        setDataGridState: setLocalState,
    } = useLocalStorage<SkyAccordionLocalStorage>({
        key: LocalStorageKeys.SKY_NET_ALL_ACCORDION_DASHBOARD_KEY,
        value: persistentKey,
        defaultValue: initialHeightState,
    })
    const [
        currentHeight,
        setCurrentHeight,
    ] = useState(initialHeightPx)

    const currentHeightPx = useMemo(() => {
        if (enablePersistence) {
            return dataGridState?.tableHeightPx
        }
        return currentHeight
    }, [
        dataGridState?.tableHeightPx,
        enablePersistence,
        currentHeight,
    ])

    const onHeightStateChange = useCallback((newHeight: number) => {
        if (enablePersistence) {
            setLocalState({
                tableHeightPx: newHeight,
            })
        } else {
            setCurrentHeight(newHeight)
        }
    }, [
        enablePersistence,
        setLocalState,
    ])

    useLayoutEffect(() => {
        const observer = new ResizeObserver((entries) => {
            // eslint-disable-next-line no-restricted-syntax
            for (const entry of entries) {
                const newHeightPx = entry.contentRect.height

                onHeightStateChange(newHeightPx)
            }
        })

        if (containerRef.current) {
            observer.observe(containerRef.current)
        }

        return () => { return observer.disconnect() }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return {
        containerRef,
        currentHeightPx,
    }
}

export default usePersistedResize
